import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {
	GrowableTableRowItem,
	InvisibleTableRowItem,
	NumberColumn,
	Resizer,
	StaticTableRowItem,
	TableHeaderItemContainer,
	TableRowHeader,
} from './task-row.styled';
import {SortDirectionIcon} from '../../icons/sort-direction-icon';

export const TaskRowHeader = ({intl, columns, sortBy, onSortByChange, stickyTopPosition, isInContainer, tracking}) => {
	const taskNameColumn = useRef(null);
	const [taskWidth, setTaskWidth] = useState(null);

	const [originalTaskWidth, _setOriginalTaskWidth] = useState(0);
	const originalTaskWidthRef = useRef(originalTaskWidth);
	const setOriginalTaskWidth = data => {
		originalTaskWidthRef.current = data;
		_setOriginalTaskWidth(data);
	};

	const [mouseDownPositionX, _setMouseDownPositionX] = useState(-1);
	const mouseDownPositionXRef = useRef(mouseDownPositionX);
	const setMouseDownPositionX = data => {
		mouseDownPositionXRef.current = data;
		_setMouseDownPositionX(data);
	};

	const handleSortByClick = name => {
		// not allowing sort by
		if (!onSortByChange) {
			return;
		}
		const sortByCopy = {...sortBy};
		if (name === sortByCopy.column) {
			if (sortByCopy.ascending) {
				sortByCopy.ascending = false;
			} else {
				sortByCopy.column = null;
			}
		} else {
			sortByCopy.column = name;
			sortByCopy.ascending = true;
		}

		onSortByChange(sortByCopy);
	};

	const isSortedColumn = name => {
		return sortBy.column === name;
	};

	const getSortDirection = () => {
		return sortBy.ascending ? SortDirectionIcon.DIRECTION.UP : SortDirectionIcon.DIRECTION.DOWN;
	};
	const isSortable = () => {
		return !!onSortByChange;
	};

	const onMouseMove = e => {
		if (mouseDownPositionXRef.current > 0) {
			const distance = e.x - mouseDownPositionXRef.current;
			const newVal = originalTaskWidthRef.current + distance;
			if (taskWidth === null) {
				//there is a 32px jump I haven't identified yet
				setTaskWidth(newVal > 200 ? newVal - 32 : null);
			} else {
				setTaskWidth(newVal > 200 ? newVal : null);
			}
		}
	};

	const onMouseUpHandler = () => {
		setMouseDownPositionX(-1);
		tracking.trackEvent('Task Name resized');
		document.getElementById('app').style.cursor = 'inherit';
		window.removeEventListener('mouseup', onMouseUpHandler);
		window.removeEventListener('mousemove', onMouseMove);
		return false;
	};

	const handleMouseDown = e => {
		setMouseDownPositionX(e.nativeEvent.x);
		setOriginalTaskWidth(taskWidth || taskNameColumn.current.getBoundingClientRect().width);
		document.getElementById('app').style.cursor = 'col-resize';
		window.addEventListener('mouseup', onMouseUpHandler);
		window.addEventListener('mousemove', onMouseMove);
	};

	return (
		<TableRowHeader stickyTopPosition={stickyTopPosition} isInContainer={isInContainer}>
			{columns.checkbox && <StaticTableRowItem width={18}></StaticTableRowItem>}
			{columns.projectId && (
				<StaticTableRowItem width={50}>
					<TableHeaderItemContainer
						onClick={() => handleSortByClick('project')}
						sort={isSortedColumn('project', sortBy)}
						sortable={isSortable()}
					>
						<p>{intl.formatMessage({id: 'common.project_id_short'})}</p>
						{isSortedColumn('project') && (
							<SortDirectionIcon size={SortDirectionIcon.SIZE.STANDARD} direction={getSortDirection()} />
						)}
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}
			{columns.projectName && (
				<StaticTableRowItem width={166}>
					<TableHeaderItemContainer
						onClick={() => handleSortByClick('project-name')}
						sort={isSortedColumn('project-name', sortBy)}
						sortable={isSortable}
					>
						<p>{intl.formatMessage({id: 'common.project-name'})}</p>
						{isSortedColumn('project-name') && (
							<SortDirectionIcon size={SortDirectionIcon.SIZE.STANDARD} direction={getSortDirection()} />
						)}
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}
			{columns.taskId && (
				<StaticTableRowItem width={50}>
					<TableHeaderItemContainer
						onClick={() => handleSortByClick('id')}
						sort={isSortedColumn('id')}
						sortable={isSortable()}
					>
						<p>{intl.formatMessage({id: 'common.id'})}</p>
						{isSortedColumn('id') && <SortDirectionIcon direction={getSortDirection()} />}
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			<GrowableTableRowItem ref={taskNameColumn} width={taskWidth} resizable={true} resizing={mouseDownPositionX > -1}>
				<div className={'header'}>
					<TableHeaderItemContainer
						onClick={() => handleSortByClick('name')}
						sortable={isSortable()}
						sort={isSortedColumn('name')}
					>
						<p>{intl.formatMessage({id: 'common.name'})}</p>
						{isSortedColumn('name') && (
							<SortDirectionIcon direction={getSortDirection()} size={SortDirectionIcon.SIZE.STANDARD} />
						)}
					</TableHeaderItemContainer>
					<Resizer
						className={'resizer'}
						data-userpilot={'task-resizer'}
						onMouseDown={e => handleMouseDown(e)}
					></Resizer>
				</div>
			</GrowableTableRowItem>

			{columns.assignedPerson && (
				<StaticTableRowItem width={166}>
					<TableHeaderItemContainer
						onClick={() => handleSortByClick('assigned')}
						sort={isSortedColumn('assigned')}
						sortable={isSortable()}
					>
						<p>{intl.formatMessage({id: 'common.assignees'})}</p>
						{isSortedColumn('assigned') && <SortDirectionIcon direction={getSortDirection()} />}
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			{columns.owner && <StaticTableRowItem width={166}>{intl.formatMessage({id: 'common.owner'})}</StaticTableRowItem>}

			{columns.client && (
				<StaticTableRowItem width={166}>
					<TableHeaderItemContainer
						onClick={() => handleSortByClick('client')}
						sort={isSortedColumn('client', sortBy)}
						sortable={isSortable}
					>
						<p>{intl.formatMessage({id: 'common.client'})}</p>
						{isSortedColumn('client') && (
							<SortDirectionIcon size={SortDirectionIcon.SIZE.STANDARD} direction={getSortDirection()} />
						)}
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			{columns.status && (
				<StaticTableRowItem width={166} data-userpilot={'task-row-header-status'}>
					<TableHeaderItemContainer
						onClick={() => handleSortByClick('status')}
						sort={isSortedColumn('status')}
						sortable={isSortable()}
					>
						<p>{intl.formatMessage({id: 'common.status'})}</p>
						{isSortedColumn('status') && <SortDirectionIcon direction={getSortDirection()} />}
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			{columns.startDate && (
				<StaticTableRowItem width={90}>
					<TableHeaderItemContainer
						onClick={() => handleSortByClick('start_date')}
						sort={isSortedColumn('start_date')}
						sortable={isSortable()}
					>
						<p>{intl.formatMessage({id: 'common.start_date'})}</p>
						{isSortedColumn('start_date') && <SortDirectionIcon direction={getSortDirection()} />}
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			{columns.deadline && (
				<StaticTableRowItem width={90}>
					<TableHeaderItemContainer
						onClick={() => handleSortByClick('deadline')}
						sort={isSortedColumn('deadline')}
						sortable={isSortable()}
					>
						<p>{intl.formatMessage({id: 'common.deadline'})}</p>
						{isSortedColumn('deadline') && <SortDirectionIcon direction={getSortDirection()} />}
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			{columns.phase && (
				<StaticTableRowItem width={166}>
					<TableHeaderItemContainer
						onClick={() => handleSortByClick('phase')}
						sort={isSortedColumn('phase')}
						sortable={isSortable()}
					>
						<p>{intl.formatMessage({id: 'common.phase'})}</p>
						{isSortedColumn('phase') && <SortDirectionIcon direction={getSortDirection()} />}
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			{columns.progress && (
				<StaticTableRowItem width={70} title={intl.formatMessage({id: 'common.progress'})}>
					<TableHeaderItemContainer>
						<NumberColumn>{intl.formatMessage({id: 'common.progress.short'})}</NumberColumn>
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			{columns.estimate && (
				<StaticTableRowItem width={60}>
					<TableHeaderItemContainer>
						<NumberColumn>{intl.formatMessage({id: 'common.estimate'})}</NumberColumn>
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			{columns.timeEntries && (
				<StaticTableRowItem width={80}>
					<TableHeaderItemContainer>
						<NumberColumn>{intl.formatMessage({id: 'common.time_entries'})}</NumberColumn>
						{isSortedColumn('time entries') && <SortDirectionIcon direction={getSortDirection()} />}
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			{columns.remaining && (
				<StaticTableRowItem width={60}>
					<TableHeaderItemContainer>
						<NumberColumn>{intl.formatMessage({id: 'common.remaining'})}</NumberColumn>
						{isSortedColumn('remaining') && <SortDirectionIcon direction={getSortDirection()} />}
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			{columns.labels && (
				<StaticTableRowItem width={166}>
					<TableHeaderItemContainer>
						<p>{intl.formatMessage({id: 'common.labels'})}</p>
					</TableHeaderItemContainer>
				</StaticTableRowItem>
			)}

			{columns.star && <StaticTableRowItem width={14} />}
			<InvisibleTableRowItem width={16} />
			<InvisibleTableRowItem width={14} />
		</TableRowHeader>
	);
};

TaskRowHeader.propTypes = {
	intl: PropTypes.shape({
		formatMessage: PropTypes.func,
	}),

	/**
	 * value of the css element top if the header should be on position sticky
	 */
	stickyTopPosition: PropTypes.number,

	/**
	 * Elements to show (or not) in the row
	 */
	columns: PropTypes.shape({
		checkbox: PropTypes.bool,
		projectId: PropTypes.bool,
		projectName: PropTypes.bool,
		taskId: PropTypes.bool,
		assignedPerson: PropTypes.bool,
		owner: PropTypes.bool,
		client: PropTypes.bool,
		status: PropTypes.bool,
		startDate: PropTypes.bool,
		deadline: PropTypes.bool,
		phase: PropTypes.bool,
		progress: PropTypes.bool,
		estimate: PropTypes.bool,
		timeEntries: PropTypes.bool,
		remaining: PropTypes.bool,
		labels: PropTypes.bool,
		star: PropTypes.bool,
	}).isRequired,
	onSortByChange: PropTypes.func,
	/**
	 * Neccessary information to know the context of the list.
	 * Lists in a containers are the one in sprints or miletsones.
	 */
	isInContainer: PropTypes.bool,
	tracking: PropTypes.object,
	sortBy: PropTypes.shape({
		column: PropTypes.string,
		ascending: PropTypes.bool,
	}),
};

TaskRowHeader.defaultProps = {
	intl: {
		formatMessage: ({id}) => id,
	},
	tracking: {
		trackEvent: ({tracked}) => tracked,
	},
	stickyTopPosition: null,
	sortBy: {column: null, ascending: true},
};
